import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { PromoBudgetService, PromoSponsorService } from 'app/services';

@Component({
    selector: 'promo-budget',
    templateUrl: './promo-budget.component.html',
    styleUrls: ['./promo-budget.component.scss',
        '../../../../styles/sidebar.scss'
    ],
    providers: [MessageService]
})
export class PromoBudgetComponent implements OnInit {

    public rowsPerPage: number = 50;
    public rowsPerPageOptions: Array<number> = [50, 100, 500, 1000];
    public totalRecords: number = 0;
    public first: number = 0;
    public sortField: string = 'Id';
    public sortOrder: number = -1;

    public loading: boolean = false;

    public budgets: Array<any> = [];
    public sponsors: Array<any> = [];

    public filter: { SearchName: string } = { SearchName: '' };
    private filterTimeout: any;

    public showEditSideBar: boolean = false;
    public budget: any = {};

    public showTopUpSideBar: boolean = false;
    public topUp: any = {};

    public cols: any[] = [
        { field: 'Id', header: 'ID', width: '70px' },
        { field: 'Name', header: 'Name' },
        { field: 'SponsorName', header: 'Sponsor' },
        { field: 'InitialAmountStr', header: 'Initial', width: '120px' },
        { field: 'CurrentBalanceStr', header: 'Balance', width: '120px' },
        { field: 'Currency', header: 'Currency', width: '90px' }
    ];

    constructor(
        private messageService: MessageService,
        private promoBudgetService: PromoBudgetService,
        private promoSponsorService: PromoSponsorService) {
    }

    ngOnInit(): void {
        this.loadSponsors();
        this.getBudgets(0);
    }

    public loadSponsors(): void {
        const dto = {
            SearchName: '',
            IsActive: true,
            Skip: 0,
            Take: 1000,
            SortField: 'Name',
            SortOrder: 1
        };

        this.promoSponsorService.getSponsors(dto, this.messageService)
            .then(data => {
                if (data && data.Success) {
                    this.sponsors = data.Items || [];
                }
            });
    }

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        const skip = event.first ? event.first : 0;

        if (event.rows) {
            this.rowsPerPage = event.rows;
        }

        if (!event.sortField) {
            this.sortField = 'Id';
            this.sortOrder = -1;
        }
        else {
            this.sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
            this.sortOrder = event.sortOrder ? event.sortOrder : 1;
        }

        this.getBudgets(skip);
    }

    public getBudgets(skip: number): void {
        this.loading = true;

        const dto = {
            SearchName: this.filter.SearchName,
            Skip: skip,
            Take: this.rowsPerPage,
            SortField: this.sortField,
            SortOrder: this.sortOrder
        };

        this.promoBudgetService.getBudgets(dto, this.messageService)
            .then(data => {
                if (!data || !data.Success) {
                    this.loading = false;
                    if (data) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    }
                    return;
                }

                this.first = skip;
                this.totalRecords = data.TotalRecords;
                this.budgets = (data.Items || []).map((x: any) => {
                    x.InitialAmountStr = this.formatMoney(x.InitialAmount);
                    x.CurrentBalanceStr = this.formatMoney(x.CurrentBalance);
                    return x;
                });
                this.loading = false;
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    private formatMoney(value: number): string {
        if (value === null || value === undefined) {
            return '';
        }
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    public handleHeaderFilterChange(): void {
        clearTimeout(this.filterTimeout);
        this.filterTimeout = setTimeout(() => {
            this.getBudgets(0);
        }, 800);
    }

    public openCreate(): void {
        this.budget = { Id: 0, Name: '', SponsorId: null, InitialAmount: 0, Currency: 'PHP', IsActive: true };
        this.showEditSideBar = true;
    }

    public openEdit(item: any): void {
        this.budget = {
            Id: item.Id,
            Name: item.Name,
            SponsorId: item.SponsorId,
            InitialAmount: item.InitialAmount,
            CurrentBalance: item.CurrentBalance,
            Currency: item.Currency,
            IsActive: item.IsActive
        };
        this.showEditSideBar = true;
    }

    public closeSideBar(): void {
        this.showEditSideBar = false;
    }

    public save(): void {
        if (!this.budget.Name || !this.budget.Name.trim()) {
            this.messageService.add({ severity: 'error', summary: 'Warning', detail: 'Name is required.' });
            return;
        }
        if (this.budget.InitialAmount === null || this.budget.InitialAmount === undefined || this.budget.InitialAmount < 0) {
            this.messageService.add({ severity: 'error', summary: 'Warning', detail: 'Initial amount must be zero or greater.' });
            return;
        }

        this.loading = true;

        this.promoBudgetService.addOrUpdateBudget(this.budget, this.messageService)
            .then(data => {
                this.loading = false;
                if (!data) {
                    return;
                }
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Budget saved.' });
                this.showEditSideBar = false;
                this.getBudgets(this.first);
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    public switchActive(item: any): void {
        const dto = {
            PromoBudgetId: item.Id,
            IsActive: item.IsActive
        };

        this.promoBudgetService.setBudgetActive(dto, this.messageService)
            .then(data => {
                if (!data || !data.Success) {
                    item.IsActive = !item.IsActive;
                    if (data) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    }
                }
            })
            .catch(err => {
                item.IsActive = !item.IsActive;
                throw err;
            });
    }

    public openTopUp(item: any): void {
        this.topUp = { PromoBudgetId: item.Id, BudgetName: item.Name, Amount: 0, Note: '' };
        this.showTopUpSideBar = true;
    }

    public closeTopUp(): void {
        this.showTopUpSideBar = false;
    }

    public saveTopUp(): void {
        if (!this.topUp.Amount || this.topUp.Amount === 0) {
            this.messageService.add({ severity: 'error', summary: 'Warning', detail: 'Enter a non-zero amount.' });
            return;
        }

        this.loading = true;

        const dto = {
            PromoBudgetId: this.topUp.PromoBudgetId,
            Amount: this.topUp.Amount,
            Note: this.topUp.Note
        };

        this.promoBudgetService.topUpBudget(dto, this.messageService)
            .then(data => {
                this.loading = false;
                if (!data) {
                    return;
                }
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Balance adjusted.' });
                this.showTopUpSideBar = false;
                this.getBudgets(this.first);
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }
}
